import { useCallback, useEffect, useRef, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  PageHeader,
  TopBar,
  FooterStrip,
  YearLevelChips,
  YearChip,
  CSVExportButton,
  PrintPDFButton,
  TermToggle,
  TooltipProvider,
} from "@/components/branded/Components";
import type { TermId } from "@/types";
import { TERM_DEFAULT } from "@/data/tokens";
import { availableTermIds, defaultAvailableTermId } from "@/lib/report-data";
import { downloadCsv, schoolStudentCsvRows } from "@/lib/class-csv";
import { useTweaks } from "@/lib/useTweaks";
import { ROLE_HOMES, type Role } from "@/data/role-defaults";
import { RoleProvider } from "@/lib/role-context";
import { PortalHome } from "@/pages/PortalHomePage";
import { DistrictPage, SchoolPage } from "@/pages/DistrictSchoolPages";
import { ClassPage } from "@/pages/ClassPage";
import { StudentPage } from "@/pages/StudentPage";
import { LoginPage } from "@/pages/LoginPage";
import { SessionGate } from "@/components/SessionGate";
import { BetaNoticeBanner } from "@/components/branded/BetaNoticeBanner";
import { TweaksPanel } from "@/components/tweaks/TweaksPanel";
import ThemeSmoke from "@/pages/ThemeSmoke";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { getRuntimeConfig, navigationFromBootstrap } from "@/config/runtime";
import { getPortalAdapter } from "@/api/portal-adapter";
import { usePortalData } from "@/hooks/usePortalData";
import { TrackingProvider, useTracker } from "@/hooks/useTracker";
import type { PortalNavigationResponse } from "@/contracts/portal";
import {
  mapPortalClassToUi,
  mapPortalDistrictToUi,
  mapPortalSchoolToUi,
  mapPortalStudentToUi,
} from "@/mappers/portal-to-ui";

/* ============================================================
   App.tsx — extracted from legacy/index.html inline <script>
   (lines 142–333) and re-cast onto react-router-dom v6.

   Round 5 locked decisions:
   - Printing fires window.print() in place from the student page
     (no /print route); print.css handles the 2-page layout.
   - Role lives in useTweaks (3-tier precedence). Switching role
     navigates to that role's default landing.
   ============================================================ */

const displayClassName = (name: string) => name.replaceAll("_", " ");

const TARGET_TO_PATH = (t: ReturnType<typeof homeForRole>) => {
  switch (t.level) {
    case "home": return "/";
    case "district": return `/districts/${t.districtId}`;
    case "school": return `/districts/${t.districtId}/schools/${t.schoolId}`;
    case "class": return `/districts/${t.districtId}/schools/${t.schoolId}/classes/${t.classId}`;
    case "student": return `/districts/${t.districtId}/schools/${t.schoolId}/classes/${t.classId}/students/${t.studentId}`;
  }
};

function homeForRole(role: Role) {
  return ROLE_HOMES[role];
}

export default function App() {
  return (
    <TooltipProvider>
      <AppRoutes />
    </TooltipProvider>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Phase 3 token smoke — no chrome, no role redirect */}
      <Route
        path="/theme-smoke"
        element={
          <ErrorBoundary>
            <ThemeSmoke />
          </ErrorBoundary>
        }
      />
      {/* Login — no chrome, no auth required */}
      <Route
        path="/login"
        element={
          <ErrorBoundary>
            <LoginPage />
          </ErrorBoundary>
        }
      />
      {/* Everything else gets the AppShell, gated by SessionGate */}
      <Route
        path="*"
        element={
          <ErrorBoundary>
            <SessionGate>
              <AppShellRoutes />
            </SessionGate>
          </ErrorBoundary>
        }
      />
    </Routes>
  );
}

function AppShellRoutes() {
  const runtime = getRuntimeConfig();
  return runtime.mode === "demo" ? <DemoAppShellRoutes /> : <JanisonAppShellRoutes />;
}

function DemoAppShellRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tweaks, setTweak, resetTweaks] = useTweaks({ role: "school" });
  const role = ((tweaks.role as Role) || "school") as Role;
  const lastRoleRef = useRef<Role | null>(null);

  useEffect(() => {
    if (lastRoleRef.current !== role) {
      lastRoleRef.current = role;
      const params = new URLSearchParams(location.search);
      if (params.get("lh") === "1") return;
      navigate(TARGET_TO_PATH(homeForRole(role)), { replace: true });
    }
  }, [location.search, navigate, role]);

  return (
    <ShellFrame
      role={role}
      defaultRouteTarget={homeForRole(role)}
      footer={<FooterStrip />}
      tweaksPanel={<TweaksPanel role={role} setTweak={setTweak} resetTweaks={resetTweaks} />}
    />
  );
}

function JanisonAppShellRoutes() {
  const adapter = getPortalAdapter();
  const navigate = useNavigate();
  const location = useLocation();
  const bootstrapNavigation = navigationFromBootstrap();
  const loadNavigation = useCallback(
    () => (bootstrapNavigation ? Promise.resolve(bootstrapNavigation) : adapter.getNavigation()),
    [adapter, bootstrapNavigation],
  );
  const navigationState = usePortalData<PortalNavigationResponse>(
    loadNavigation,
    bootstrapNavigation ? "navigation:bootstrap" : "navigation:adapter",
  );
  const role = (navigationState.data?.role ?? "programme") as Role;
  const defaultRouteTarget = navigationState.data?.defaultRoute ?? { level: "home" as const };

  useEffect(() => {
    if (!navigationState.data) return;
    if (location.pathname !== "/") return;
    const target = TARGET_TO_PATH(navigationState.data.defaultRoute);
    if (target !== "/") {
      navigate(target, { replace: true });
    }
  }, [location.pathname, navigate, navigationState.data]);

  if (navigationState.loading) {
    return <div style={{ padding: 32 }}>Loading navigation…</div>;
  }

  if (navigationState.error || !navigationState.data) {
    return (
      <div style={{ padding: 32 }}>
        <p style={{ margin: "0 0 12px" }}>Unable to load navigation.</p>
        <button type="button" onClick={navigationState.reload}>Try again</button>
      </div>
    );
  }

  const suppressInitialPageView = location.pathname === "/" && TARGET_TO_PATH(defaultRouteTarget) !== "/";

  return (
    <TrackingProvider suppressPageView={suppressInitialPageView}>
      <ShellFrame role={role} defaultRouteTarget={defaultRouteTarget} />
    </TrackingProvider>
  );
}

interface ShellFrameProps {
  role: Role;
  defaultRouteTarget: { level: "home" | "district" | "school" | "class" | "student"; districtId?: string; schoolId?: string; classId?: string; studentId?: string };
  footer?: React.ReactNode;
  tweaksPanel?: React.ReactNode;
}

function ShellFrame({ role, defaultRouteTarget, footer, tweaksPanel }: ShellFrameProps) {
  const location = useLocation();
  const [yearFilter, setYearFilter] = useState<"F" | "Y1">("F");
  const [term] = useState<string>(TERM_DEFAULT);
  const mainRef = useRef<HTMLElement | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [location.pathname, role]);

  useEffect(() => {
    document.body.classList.toggle("sidebar-open", mobileMenuOpen);
  }, [mobileMenuOpen]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional close-on-navigate
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <RoleProvider value={role}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {/* Skip-to-content link — hidden until focused, then jumps the
            keyboard user past the page chrome straight into the main
            scrollable area. Styles in src/styles/globals.css. */}
        <a href="#main-content" className="skip-to-content">
          Skip to main content
        </a>
        <BetaNoticeBanner />
        <main
          id="main-content"
          tabIndex={-1}
          ref={mainRef}
          style={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          <Routes>
            <Route path="/" element={<HomeView role={role} yearFilter={yearFilter} setYearFilter={setYearFilter} />} />
            <Route path="/portal-home" element={<HomeView role={role} yearFilter={yearFilter} setYearFilter={setYearFilter} />} />
            <Route path="/districts" element={<Navigate to={TARGET_TO_PATH(defaultRouteTarget)} replace />} />
            <Route path="/districts/:districtId" element={<DistrictView role={role} yearFilter={yearFilter} setYearFilter={setYearFilter} />} />
            <Route path="/districts/:districtId/schools/:schoolId" element={<SchoolView role={role} yearFilter={yearFilter} setYearFilter={setYearFilter} />} />
            <Route path="/districts/:districtId/schools/:schoolId/classes/:classId" element={<ClassView role={role} term={term} />} />
            <Route path="/districts/:districtId/schools/:schoolId/classes/:classId/students/:studentId" element={<StudentView role={role} />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          {footer}
        </main>
        {tweaksPanel}
      </div>
    </RoleProvider>
  );
}

/* ────── View wrappers — own breadcrumbs + page chrome ────── */

interface ViewProps {
  role: Role;
  setTweak?: <K extends "role">(k: K, v: string) => void;
  yearFilter?: "F" | "Y1";
  setYearFilter?: (v: "F" | "Y1") => void;
  term?: string;
}

function HomeView({ role, yearFilter, setYearFilter }: ViewProps) {
  const adapter = getPortalAdapter();
  const navigate = useNavigate();
  const { track } = useTracker();
  const loadHome = useCallback(() => adapter.getPortalHome(), [adapter]);
  const { data, loading, error } = usePortalData(loadHome, "home");
  const districts = data?.districts.map(mapPortalDistrictToUi) ?? [];
  const visibleCrumbs = filterCrumbsForRole(role, [
    { key: "home", label: "Portal home", target: { level: "home" as const } },
  ]);
  return (
    <ChromeWrap
      visibleCrumbs={visibleCrumbs}
      eyebrow="Programme overview"
      title="ENSSA reporting portal"
      controls={
        <>
          {yearFilter && setYearFilter && <YearLevelChips value={yearFilter} onChange={setYearFilter} availableYears={undefined} />}
        </>
      }
    >
      {loading ? <div style={{ padding: 32 }}>Loading…</div> : null}
      {error ? <div style={{ padding: 32 }}>Unable to load portal overview.</div> : null}
      {data ? (
        <PortalHome
          districts={districts}
          onOpenDistrict={(id: string) => {
            track("district_opened", { district_id: id });
            navigate(`/districts/${id}`);
          }}
        />
      ) : null}
    </ChromeWrap>
  );
}

function DistrictView({ role, yearFilter, setYearFilter }: ViewProps) {
  const { districtId } = useParams();
  const adapter = getPortalAdapter();
  const navigate = useNavigate();
  const { track } = useTracker();
  const loadDistrict = useCallback(
    () => adapter.getDistrictReport(districtId ?? ""),
    [adapter, districtId],
  );
  const loadPopulationReference = useCallback(() => adapter.getPopulationReference?.() ?? Promise.resolve(null), [adapter]);
  const { data: populationReference } = usePortalData(loadPopulationReference, "population-reference");
  const { data, loading, error } = usePortalData(loadDistrict, `district:${districtId ?? ""}`);
  const district = data?.district ? mapPortalDistrictToUi(data.district) : null;
  const handleYearFilterChange = useCallback((nextYearFilter: "F" | "Y1") => {
    if (!setYearFilter || !yearFilter || nextYearFilter === yearFilter) return;
    track("year_filter_changed", { year_level: nextYearFilter });
    setYearFilter(nextYearFilter);
  }, [setYearFilter, track, yearFilter]);
  if (!loading && !error && !district) return <NotFound />;

  const crumbs = [
    { key: "home", label: "Portal home", target: { level: "home" as const } },
    { key: "d", label: district?.name ?? "District", target: { level: "district" as const, districtId: districtId ?? "" } },
  ];
  const visibleCrumbs = filterCrumbsForRole(role, crumbs);

  return (
    <ChromeWrap
      visibleCrumbs={visibleCrumbs}
      title={district?.name ?? "District"}
      controls={
        <>
          {yearFilter && setYearFilter && <YearLevelChips value={yearFilter} onChange={handleYearFilterChange} availableYears={undefined} />}
          <CSVExportButton scope={undefined} />
        </>
      }
    >
      {loading ? <div style={{ padding: 32 }}>Loading…</div> : null}
      {error ? <div style={{ padding: 32 }}>Unable to load district.</div> : null}
      {district ? (
        <>
          <DistrictPage
            district={district}
            yearFilter={yearFilter}
            populationReference={populationReference}
            onOpenSchool={(id: string) => {
              track("school_opened", {
                district_id: district.id,
                school_id: id,
              });
              navigate(`/districts/${district.id}/schools/${id}`);
            }}
          />
        </>
      ) : null}
    </ChromeWrap>
  );
}

function SchoolView({ role }: ViewProps) {
  const { districtId, schoolId } = useParams();
  const adapter = getPortalAdapter();
  const navigate = useNavigate();
  const { track } = useTracker();
  const loadSchool = useCallback(
    () => adapter.getSchoolReport(districtId ?? "", schoolId ?? ""),
    [adapter, districtId, schoolId],
  );
  const { data, loading, error } = usePortalData(loadSchool, `school:${districtId ?? ""}:${schoolId ?? ""}`);
  const district = data?.district ? mapPortalDistrictToUi(data.district) : null;
  const school = data?.school ? mapPortalSchoolToUi(data.school) : null;
  if (!loading && !error && (!district || !school)) return <NotFound />;

  const crumbs = [
    { key: "home", label: "Portal home", target: { level: "home" as const } },
    { key: "d", label: district?.name ?? "District", target: { level: "district" as const, districtId: districtId ?? "" } },
    { key: "s", label: school?.name ?? "School", target: { level: "school" as const, districtId: districtId ?? "", schoolId: schoolId ?? "" } },
  ];
  const visibleCrumbs = filterCrumbsForRole(role, crumbs);

  return (
    <ChromeWrap
      visibleCrumbs={visibleCrumbs}
      title={school?.name ?? "School"}
      controls={school ? <CSVExportButton scope="school" onExport={() => downloadCsv(`${school.id}-${TERM_DEFAULT}-student-results.csv`, schoolStudentCsvRows(school, TERM_DEFAULT))} /> : null}
    >
      {loading ? <div style={{ padding: 32 }}>Loading…</div> : null}
      {error ? <div style={{ padding: 32 }}>Unable to load school.</div> : null}
      {district && school ? (
        <>
          <SchoolPage
            school={school}
            district={district}
            onOpenClass={(id: string) => {
              track("class_opened", {
                district_id: district.id,
                school_id: school.id,
                class_id: id,
              });
              navigate(`/districts/${district.id}/schools/${school.id}/classes/${id}`);
            }}
          />
        </>
      ) : null}
    </ChromeWrap>
  );
}

function ClassView({ role, term }: ViewProps) {
  const { districtId, schoolId, classId } = useParams();
  const adapter = getPortalAdapter();
  const navigate = useNavigate();
  const loadClass = useCallback(
    () => adapter.getClassReport(districtId ?? "", schoolId ?? "", classId ?? ""),
    [adapter, districtId, schoolId, classId],
  );
  const { data, loading, error } = usePortalData(loadClass, `class:${districtId ?? ""}:${schoolId ?? ""}:${classId ?? ""}`);
  const district = data?.district ? mapPortalDistrictToUi(data.district) : null;
  const school = data?.school ? mapPortalSchoolToUi(data.school) : null;
  const klass = data?.klass ? mapPortalClassToUi(data.klass) : null;
  if (!loading && !error && (!district || !school || !klass)) return <NotFound />;

  const crumbs = [
    { key: "home", label: "Portal home", target: { level: "home" as const } },
    { key: "d", label: district?.name ?? "District", target: { level: "district" as const, districtId: districtId ?? "" } },
    { key: "s", label: school?.name ?? "School", target: { level: "school" as const, districtId: districtId ?? "", schoolId: schoolId ?? "" } },
    {
      key: "c",
      label: klass ? `${klass.year === "F" ? "Foundation" : "Year 1"} · ${displayClassName(klass.name)}` : "Class",
      target: { level: "class" as const, districtId: districtId ?? "", schoolId: schoolId ?? "", classId: classId ?? "" },
    },
  ];
  const visibleCrumbs = filterCrumbsForRole(role, crumbs);

  return (
    <ChromeWrap
      visibleCrumbs={visibleCrumbs}
      eyebrow={school?.name ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {school.name}
          {klass && <YearChip year={klass.year} />}
        </span>
      ) : undefined}
      title={klass ? (
        <>
          <span style={{ fontWeight: 900 }}>{displayClassName(klass.name)}</span>
          {klass.teacher ? <span style={{ fontWeight: 500, color: 'var(--fg-2)', marginLeft: 8 }}>· {klass.teacher}</span> : null}
        </>
      ) : "Class"}
      controls={<PrintPDFButton />}
    >
      {loading ? <div style={{ padding: 32 }}>Loading…</div> : null}
      {error ? <div style={{ padding: 32 }}>Unable to load class.</div> : null}
      {district && school && klass ? (
        <>
          <ClassPage
            school={school}
            klass={klass}
            term={term}
            onOpenStudent={(id: string, nextTerm?: TermId | string) => {
              const search = nextTerm && nextTerm !== TERM_DEFAULT ? `?term=${encodeURIComponent(String(nextTerm))}` : '';
              navigate(
                `/districts/${district.id}/schools/${school.id}/classes/${klass.id}/students/${id}${search}`,
              );
            }}
          />
        </>
      ) : null}
    </ChromeWrap>
  );
}

function StudentView({ role }: ViewProps) {
  const { districtId, schoolId, classId, studentId } = useParams();
  const adapter = getPortalAdapter();
  const { track } = useTracker();
  const loadStudent = useCallback(
    () => adapter.getStudentReport(districtId ?? "", schoolId ?? "", classId ?? "", studentId ?? ""),
    [adapter, districtId, schoolId, classId, studentId],
  );
  const loadHome = useCallback(() => adapter.getPortalHome(), [adapter]);
  const loadPopulationReference = useCallback(() => adapter.getPopulationReference?.() ?? Promise.resolve(null), [adapter]);
  const { data: homeData } = usePortalData(loadHome, "home:student-comparison-reference");
  const { data: populationReference } = usePortalData(loadPopulationReference, "population-reference");
  const { data, loading, error } = usePortalData(loadStudent, `student:${districtId ?? ""}:${schoolId ?? ""}:${classId ?? ""}:${studentId ?? ""}`);
  const district = data?.district ? mapPortalDistrictToUi(data.district) : null;
  const school = data?.school ? mapPortalSchoolToUi(data.school) : null;
  const klass = data?.klass ? mapPortalClassToUi(data.klass) : null;
  const student = data?.student ? mapPortalStudentToUi(data.student) : null;
  const comparisonDistricts = homeData?.districts.map(mapPortalDistrictToUi) ?? (district ? [district] : []);
  const viewedStudentIdRef = useRef<string | null>(null);

  /* Term lives here so the TermToggle can sit inside the PageHeader
     controls strip (alongside Print PDF) rather than inside an inner
     student-header card. URL-driven so refresh/print preserve state. */
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTerm = searchParams.get('term');
  // Student reports should keep partially-completed screening windows visible
  // so teachers can review attempted subtests even when no composite score is available.
  const availableStudentTerms = student ? availableTermIds([student], false) : [TERM_DEFAULT];
  const term: TermId | string = urlTerm && availableStudentTerms.includes(urlTerm) ? urlTerm : defaultAvailableTermId(availableStudentTerms);
  const setTerm = (next: TermId) => {
    if (next === term) return;
    track("student_term_changed", { term: String(next) });
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev);
      if (next === TERM_DEFAULT) out.delete('term'); else out.set('term', String(next));
      return out;
    }, { replace: true });
  };

  useEffect(() => {
    if (!school || !klass || !student) return;
    if (viewedStudentIdRef.current === student.id) return;
    viewedStudentIdRef.current = student.id;
    track("student_viewed", {
      student_id: student.id,
      class_id: klass.id,
      risk_band: student.risk,
    });
  }, [klass, school, student, track]);

  if (!loading && !error && (!district || !school || !klass || !student)) return <NotFound />;

  const crumbs = [
    { key: "home", label: "Portal home", target: { level: "home" as const } },
    { key: "d", label: district?.name ?? "District", target: { level: "district" as const, districtId: districtId ?? "" } },
    { key: "s", label: school?.name ?? "School", target: { level: "school" as const, districtId: districtId ?? "", schoolId: schoolId ?? "" } },
    {
      key: "c",
      label: klass ? `${klass.year === "F" ? "Foundation" : "Year 1"} · ${displayClassName(klass.name)}` : "Class",
      target: { level: "class" as const, districtId: districtId ?? "", schoolId: schoolId ?? "", classId: classId ?? "" },
    },
    {
      key: "u",
      label: student?.name ?? "Student",
      target: { level: "student" as const, districtId: districtId ?? "", schoolId: schoolId ?? "", classId: classId ?? "", studentId: studentId ?? "" },
    },
  ];
  const visibleCrumbs = filterCrumbsForRole(role, crumbs);

  return (
    <ChromeWrap
      visibleCrumbs={visibleCrumbs}
      eyebrow={school?.name ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {school.name}
          {klass && <YearChip year={klass.year} />}
        </span>
      ) : undefined}
      title={student?.name ?? "Student report"}
      controls={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {availableStudentTerms.length > 1 ? <TermToggle value={term} availableTerms={availableStudentTerms} onChange={setTerm} label="Screening period" /> : null}
          <PrintPDFButton />
        </span>
      }
    >
      {loading ? <div style={{ padding: 32 }}>Loading…</div> : null}
      {error ? <div style={{ padding: 32 }}>Unable to load student report.</div> : null}
      {school && klass && student ? (
        <>
          <StudentPage
            school={school}
            klass={klass}
            student={student}
            term={term}
            districts={comparisonDistricts}
            populationReference={populationReference}
          />
        </>
      ) : null}
    </ChromeWrap>
  );
}

function NotFound() {
  return (
    <ChromeWrap
      visibleCrumbs={[]}
      title="Page not found"
      controls={null}
    >
      <div style={{ padding: 32 }}>Page not found.</div>
    </ChromeWrap>
  );
}

/* ────── Page chrome wrapper ────── */

type CrumbTarget =
  | { level: "home" }
  | { level: "district"; districtId: string }
  | { level: "school"; districtId: string; schoolId: string }
  | { level: "class"; districtId: string; schoolId: string; classId: string }
  | { level: "student"; districtId: string; schoolId: string; classId: string; studentId: string };

interface VisibleCrumb {
  key: string;
  label: string;
  target: CrumbTarget;
}

interface ChromeWrapProps {
  /* role + setTweak no longer needed here — the role switcher moved to
     FooterStrip which gets them directly from AppShellRoutes. View
     wrappers still own filterCrumbsForRole which is the only remaining
     role-aware concern in this layer. */
  visibleCrumbs: VisibleCrumb[];
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  controls?: React.ReactNode;
  stats?: React.ReactNode;
  children: React.ReactNode;
}

function ChromeWrap({
  visibleCrumbs,
  eyebrow,
  title,
  controls,
  stats,
  children,
}: ChromeWrapProps) {
  const crumbItems = visibleCrumbs.map((c) => ({ key: c.key, label: c.label, href: targetToPath(c.target) }));
  return (
    <>
      <TopBar visibleCrumbs={crumbItems} />
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        controls={controls}
        stats={stats}
      />
      <div
        style={{
          padding: "24px 32px 48px",
          width: "100%",
          boxSizing: "border-box",
          maxWidth: 1280,
          margin: "0 auto",
          flex: 1,
        }}
      >
        {children}
      </div>
    </>
  );
}

function targetToPath(target: CrumbTarget): string {
  switch (target.level) {
    case "home": return "/";
    case "district": return `/districts/${target.districtId}`;
    case "school": return `/districts/${target.districtId}/schools/${target.schoolId}`;
    case "class": return `/districts/${target.districtId}/schools/${target.schoolId}/classes/${target.classId}`;
    case "student": return `/districts/${target.districtId}/schools/${target.schoolId}/classes/${target.classId}/students/${target.studentId}`;
    default: return "/";
  }
}

function filterCrumbsForRole(
  role: Role,
  crumbs: VisibleCrumb[],
): VisibleCrumb[] {
  if (role === "teacher") {
    const i = crumbs.findIndex((c) => c.key === "c");
    return i >= 0 ? crumbs.slice(i) : crumbs;
  }
  if (role === "school") {
    const i = crumbs.findIndex((c) => c.key === "s");
    return i >= 0 ? crumbs.slice(i) : crumbs;
  }
  if (role === "district") {
    const i = crumbs.findIndex((c) => c.key === "d");
    return i >= 0 ? crumbs.slice(i) : crumbs;
  }
  return crumbs; // programme sees everything
}
